import * as THREE from 'three';
import { AVATAR } from '../config.js';

/**
 * 操作キャラクター（キンクマハムスター）。
 *
 * 飼い主から預かった写真に寄せて作っている。
 * - 薄いアプリコット色の毛で、口まわりと腹が白っぽい
 * - 体は低く丸く、頭が大きい。耳は薄い茶灰色で丸い
 * - 目は大きく黒くつややか、鼻と手足はピンク
 *
 * 外部の3Dモデルを持ってくるとライセンスと配信容量の問題が出るため、
 * 球体の組み合わせで作る。追加のダウンロードが発生しない。
 *
 * @param {{ lights?: boolean }} options lights=false にすると、キャラ付属のライトを付けない
 *   （部屋側にライトがある CG の物件で、二重に照らして白飛びしないため）
 */
export function createAvatar({ lights = true } = {}) {
  const root = new THREE.Group();
  const C = AVATAR.colors;

  const fur = matte(C.fur);
  const cream = matte(C.belly);
  const earOuter = matte(C.earOuter);
  const earIn = matte(C.earInner);
  const eyeMat = new THREE.MeshPhongMaterial({ color: C.eye, shininess: 90, specular: 0x666666 });
  const white = matte(0xffffff);
  const pink = matte(C.nose);
  const paw = matte(C.paw);

  // --- 胴体。写真のように低く、後ろが丸く膨らんだ形 ---
  const body = new THREE.Mesh(sphere(1, 24, 16), fur);
  body.scale.set(0.31, 0.25, 0.40);
  body.position.set(0, 0.25, -0.04);
  root.add(body);

  const rump = new THREE.Mesh(sphere(1, 20, 14), fur);
  rump.scale.set(0.29, 0.24, 0.26);
  rump.position.set(0, 0.24, -0.20);
  root.add(rump);

  const bellyMesh = new THREE.Mesh(sphere(1, 18, 12), cream);
  bellyMesh.scale.set(0.24, 0.17, 0.32);
  bellyMesh.position.set(0, 0.17, 0.0);
  root.add(bellyMesh);

  // --- 頭。胴体につながる大きな頭 ---
  const head = new THREE.Group();
  head.position.set(0, 0.38, 0.22);
  root.add(head);

  const skull = new THREE.Mesh(sphere(1, 24, 16), fur);
  skull.scale.set(0.27, 0.24, 0.26);
  head.add(skull);

  // 口まわりは白っぽく、横に広い
  const muzzle = new THREE.Mesh(sphere(1, 18, 12), cream);
  muzzle.scale.set(0.16, 0.12, 0.14);
  muzzle.position.set(0, -0.07, 0.16);
  head.add(muzzle);

  const nose = new THREE.Mesh(sphere(1, 12, 8), pink);
  nose.scale.set(0.032, 0.026, 0.026);
  nose.position.set(0, -0.045, 0.29);
  head.add(nose);

  for (const side of [-1, 1]) {
    // 耳: 薄く丸く、外側は茶灰色、内側は薄いピンク灰色
    const ear = new THREE.Group();
    ear.position.set(side * 0.16, 0.19, -0.04);
    ear.rotation.z = -side * 0.35;
    ear.rotation.x = -0.2;
    head.add(ear);
    const earBack = new THREE.Mesh(sphere(1, 16, 12), earOuter);
    earBack.scale.set(0.10, 0.11, 0.035);
    ear.add(earBack);
    const earFront = new THREE.Mesh(sphere(1, 14, 10), earIn);
    earFront.scale.set(0.068, 0.078, 0.02);
    earFront.position.z = 0.022;
    ear.add(earFront);

    // 目: 大きく黒く、つやのハイライトを入れる
    const eye = new THREE.Mesh(sphere(1, 16, 12), eyeMat);
    eye.scale.setScalar(0.046);
    eye.position.set(side * 0.105, 0.02, 0.205);
    head.add(eye);
    const glint = new THREE.Mesh(sphere(1, 8, 6), white);
    glint.scale.setScalar(0.012);
    glint.position.set(side * 0.09, 0.045, 0.245);
    head.add(glint);

    // ひげ
    for (const [dy, tilt] of [[0.0, 0.15], [-0.02, 0], [-0.04, -0.15]]) {
      const whisker = new THREE.Mesh(new THREE.CylinderGeometry(0.003, 0.002, 0.18, 4), white);
      whisker.rotation.z = side * (Math.PI / 2 - 0.25);
      whisker.rotation.y = tilt;
      whisker.position.set(side * 0.12, -0.05 + dy, 0.22);
      head.add(whisker);
    }
  }

  // --- 手足。ピンクの小さな手。歩行アニメーションで前後に振る ---
  const legs = [];
  for (const side of [-1, 1]) {
    for (const [name, z, y, s] of [
      ['front', 0.20, 0.06, 0.05],
      ['back', -0.12, 0.06, 0.06],
    ]) {
      const leg = new THREE.Mesh(sphere(1, 12, 10), name === 'front' ? paw : fur);
      leg.scale.set(s, s * 0.7, s * 1.2);
      leg.position.set(side * 0.16, y, z);
      leg.userData.rest = leg.position.clone();
      leg.userData.phase = (side > 0 ? 0 : Math.PI) + (name === 'front' ? 0 : Math.PI);
      root.add(leg);
      legs.push(leg);
      if (name === 'back') {
        const foot = new THREE.Mesh(sphere(1, 10, 8), paw);
        foot.scale.set(0.035, 0.02, 0.05);
        foot.position.set(side * 0.17, 0.02, z + 0.02);
        root.add(foot);
      }
    }
  }

  // --- しっぽ ---
  const tail = new THREE.Mesh(sphere(1, 10, 8), cream);
  tail.scale.set(0.04, 0.035, 0.03);
  tail.position.set(0, 0.2, -0.44);
  root.add(tail);

  // ライトはアバターに付けて一緒に動かす。
  // スプラットやスキャンのメッシュは three のライトの影響を受けないので、部屋の見えは変わらない。
  if (lights) {
    const sky = new THREE.HemisphereLight(0xffffff, 0x6b5a48, 2.0);
    root.add(sky);
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(0.6, 1.4, 0.8);
    root.add(key);
  }

  root.scale.setScalar(AVATAR.scale);

  /** 歩いているときだけ上下に弾ませ、手足を振る */
  function update(deltaTime, { moving }) {
    const t = (update.time = (update.time ?? 0) + deltaTime * (moving ? 1 : 0.25));
    const speed = moving ? 9 : 2.2;
    const swing = moving ? 0.07 : 0;

    body.position.y = 0.25 + Math.sin(t * speed) * (moving ? 0.012 : 0.004);
    head.position.y = 0.38 + Math.sin(t * speed + 0.4) * (moving ? 0.010 : 0.005);
    head.rotation.z = Math.sin(t * speed * 0.5) * (moving ? 0.05 : 0.02);

    for (const leg of legs) {
      const rest = leg.userData.rest;
      leg.position.z = rest.z + Math.sin(t * speed + leg.userData.phase) * swing;
      leg.position.y = rest.y + Math.abs(Math.cos(t * speed + leg.userData.phase)) * swing * 0.35;
    }
  }

  /** 体の全パーツの材質。半透明にするときにまとめて変える */
  const materials = [fur, cream, earOuter, earIn, eyeMat, white, pink, paw];

  /**
   * キャラの不透明度（0〜1）。狭い場所でカメラが真後ろまで寄ると、キャラ自身が視界を塞ぐため薄くする。
   */
  function setOpacity(opacity) {
    const transparent = opacity < 0.999;
    for (const material of materials) {
      material.opacity = opacity;
      if (material.transparent !== transparent) {
        material.transparent = transparent;
        material.needsUpdate = true;
      }
      // 半透明のときは後ろの部屋が見えるよう、深度を書き込まない
      material.depthWrite = !transparent;
    }
  }

  return { root, update, setOpacity };
}

function sphere(r, w, h) {
  return new THREE.SphereGeometry(r, w, h);
}

/**
 * スプラットの中に置くので、照明の有無に左右されない MeshBasicMaterial は
 * のっぺりしすぎる。自前で上方向だけ明るくした Lambert 相当の見た目を作る。
 */
function matte(hex) {
  return new THREE.MeshLambertMaterial({ color: hex });
}
